import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, getAccessToken } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui';
import {
  Film,
  Calendar,
  MessageSquare,
  PartyPopper,
  Building2,
  Briefcase,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Plus,
  Mail,
  Users,
} from 'lucide-react';

interface DashboardStats {
  movies: {
    total: number;
    nowPlaying: number;
    comingSoon: number;
  };
  showtimes: {
    today: number;
    thisWeek: number;
  };
  inquiries: {
    contact: { new: number; total: number };
    party: { new: number; total: number };
    rental: { new: number; total: number };
  };
  jobs: {
    new: number;
    total: number;
  };
}

export function DashboardPage() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);

  const { data: stats, isLoading, error, refetch } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/dashboard/stats');
      return response.data?.data;
    },
    staleTime: 60000,
    retry: 2,
    retryDelay: 500,
    enabled: !!accessToken || !!getAccessToken(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-white">Failed to load dashboard</h2>
        <p className="text-gray-400 mt-1 mb-4">Unable to fetch statistics</p>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  const totalNewInquiries = stats
    ? stats.inquiries.contact.new + stats.inquiries.party.new + stats.inquiries.rental.new
    : 0;

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-midnight-50 to-midnight rounded-xl border border-midnight-100 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Welcome back, {user?.name?.split(' ')[0] || 'Admin'}!
            </h1>
            <p className="text-gray-400 mt-1">
              Here's what's happening at DMAX Cinema today.
            </p>
          </div>
          <div className="px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
            v2.0-AMBER
          </div>
        </div>
      </div>

      {/* Key Metrics - Large Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link
          to="/movies"
          className="group bg-midnight-50 rounded-xl border border-midnight-100 p-5 hover:border-gold/50 hover:bg-midnight-50/80 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Film className="h-5 w-5 text-green-400" />
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-gold transition-colors" />
          </div>
          <p className="text-3xl font-bold text-white">{stats?.movies.nowPlaying ?? 0}</p>
          <p className="text-sm text-gray-400 mt-1">Now Playing</p>
        </Link>

        <Link
          to="/showtimes"
          className="group bg-midnight-50 rounded-xl border border-midnight-100 p-5 hover:border-gold/50 hover:bg-midnight-50/80 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-gold transition-colors" />
          </div>
          <p className="text-3xl font-bold text-white">{stats?.showtimes.today ?? 0}</p>
          <p className="text-sm text-gray-400 mt-1">Showtimes Today</p>
        </Link>

        <Link
          to="/inquiries"
          className="group bg-midnight-50 rounded-xl border border-midnight-100 p-5 hover:border-gold/50 hover:bg-midnight-50/80 transition-all relative"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-crimson-500/10 rounded-lg">
              <MessageSquare className="h-5 w-5 text-crimson-400" />
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-gold transition-colors" />
          </div>
          <p className="text-3xl font-bold text-white">
            {stats ? stats.inquiries.contact.total + stats.inquiries.party.total + stats.inquiries.rental.total : 0}
          </p>
          <p className="text-sm text-gray-400 mt-1">Total Inquiries</p>
          {totalNewInquiries > 0 && (
            <span className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {totalNewInquiries} new
            </span>
          )}
        </Link>

        <Link
          to="/jobs"
          className="group bg-midnight-50 rounded-xl border border-midnight-100 p-5 hover:border-gold/50 hover:bg-midnight-50/80 transition-all relative"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Briefcase className="h-5 w-5 text-orange-400" />
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-gold transition-colors" />
          </div>
          <p className="text-3xl font-bold text-white">{stats?.jobs.total ?? 0}</p>
          <p className="text-sm text-gray-400 mt-1">Job Applications</p>
          {(stats?.jobs.new ?? 0) > 0 && (
            <span className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {stats?.jobs.new} new
            </span>
          )}
        </Link>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-gold" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/movies/new"
            className="flex items-center gap-3 bg-gold/10 hover:bg-gold/20 border border-gold/20 rounded-lg px-4 py-3 transition-colors group"
          >
            <Film className="h-5 w-5 text-gold" />
            <span className="font-medium text-white">Add Movie</span>
            <ChevronRight className="h-4 w-4 text-gold/50 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/showtimes"
            className="flex items-center gap-3 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg px-4 py-3 transition-colors group"
          >
            <Calendar className="h-5 w-5 text-amber-400" />
            <span className="font-medium text-white">Manage Showtimes</span>
            <ChevronRight className="h-4 w-4 text-amber-400/50 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/movies/import"
            className="flex items-center gap-3 bg-crimson-500/10 hover:bg-crimson-500/20 border border-crimson-500/20 rounded-lg px-4 py-3 transition-colors group"
          >
            <Users className="h-5 w-5 text-crimson-400" />
            <span className="font-medium text-white">Import from OMDB</span>
            <ChevronRight className="h-4 w-4 text-crimson-400/50 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/events/new"
            className="flex items-center gap-3 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 rounded-lg px-4 py-3 transition-colors group"
          >
            <PartyPopper className="h-5 w-5 text-green-400" />
            <span className="font-medium text-white">Create Event</span>
            <ChevronRight className="h-4 w-4 text-green-400/50 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Movies Status */}
        <div className="bg-midnight-50 rounded-xl border border-midnight-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Film className="h-5 w-5 text-gold" />
              Movies Overview
            </h2>
            <Link
              to="/movies"
              className="text-sm text-gold hover:text-gold-400 font-medium flex items-center gap-1"
            >
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-500/5 rounded-lg border border-green-500/10">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="text-gray-300">Now Playing</span>
              </div>
              <span className="text-xl font-bold text-white">{stats?.movies.nowPlaying ?? 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-amber-500/5 rounded-lg border border-amber-500/10">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="text-gray-300">Coming Soon</span>
              </div>
              <span className="text-xl font-bold text-white">{stats?.movies.comingSoon ?? 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-midnight rounded-lg border border-midnight-100">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-gray-400" />
                <span className="text-gray-300">Total Published</span>
              </div>
              <span className="text-xl font-bold text-white">{stats?.movies.total ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Showtimes Status */}
        <div className="bg-midnight-50 rounded-xl border border-midnight-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gold" />
              Showtimes
            </h2>
            <Link
              to="/showtimes"
              className="text-sm text-gold hover:text-gold-400 font-medium flex items-center gap-1"
            >
              Manage <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-amber-500/5 rounded-lg border border-amber-500/10">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-amber-400" />
                <span className="text-gray-300">Today</span>
              </div>
              <span className="text-xl font-bold text-white">{stats?.showtimes.today ?? 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-midnight rounded-lg border border-midnight-100">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <span className="text-gray-300">This Week</span>
              </div>
              <span className="text-xl font-bold text-white">{stats?.showtimes.thisWeek ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiries Section */}
      <div className="bg-midnight-50 rounded-xl border border-midnight-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Mail className="h-5 w-5 text-gold" />
            Customer Inquiries
          </h2>
          <Link
            to="/inquiries"
            className="text-sm text-gold hover:text-gold-400 font-medium flex items-center gap-1"
          >
            View All <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/inquiries?type=contact"
            className="group p-4 bg-midnight rounded-lg border border-midnight-100 hover:border-blue-500/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <MessageSquare className="h-5 w-5 text-amber-400" />
              {(stats?.inquiries.contact.new ?? 0) > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {stats?.inquiries.contact.new} new
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-white">{stats?.inquiries.contact.total ?? 0}</p>
            <p className="text-sm text-gray-400 mt-1 group-hover:text-amber-400 transition-colors">
              Contact Messages
            </p>
          </Link>

          <Link
            to="/inquiries?type=party"
            className="group p-4 bg-midnight rounded-lg border border-midnight-100 hover:border-pink-500/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <PartyPopper className="h-5 w-5 text-pink-400" />
              {(stats?.inquiries.party.new ?? 0) > 0 && (
                <span className="bg-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {stats?.inquiries.party.new} new
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-white">{stats?.inquiries.party.total ?? 0}</p>
            <p className="text-sm text-gray-400 mt-1 group-hover:text-pink-400 transition-colors">
              Party Zone Requests
            </p>
          </Link>

          <Link
            to="/inquiries?type=rental"
            className="group p-4 bg-midnight rounded-lg border border-midnight-100 hover:border-green-500/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <Building2 className="h-5 w-5 text-green-400" />
              {(stats?.inquiries.rental.new ?? 0) > 0 && (
                <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {stats?.inquiries.rental.new} new
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-white">{stats?.inquiries.rental.total ?? 0}</p>
            <p className="text-sm text-gray-400 mt-1 group-hover:text-green-400 transition-colors">
              Theater Rentals
            </p>
          </Link>
        </div>
      </div>
    </div>
  );
}
